import { IconEdit, IconTrash } from "@tabler/icons-react";
import { regionApi } from "apis/regionApi";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { CommonParamsType } from "constants/types/common";
import { RegionResponse } from "constants/types/region";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/Region/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification } from "utils/hooks";
import { useRegions, getRegionListKey } from "utils/hooks/request/useRegions";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useSWRConfig } from "swr";
import { Loading } from "components";
import EditForm from "../EditForm/EditForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface RegionTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const RegionTable: FC<RegionTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    regions,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useRegions(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleDeleteRegion = useCallback(
    async (regionId: string) => {
      try {
        await regionApi.delete(regionId);
        notification.success({ title: "Xóa địa bàn thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch {
        notification.error({ title: "Xóa địa bàn thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (regionId: string) => {
      openCrudModal(
        <CustomText variant="section-title">Xác nhận xóa địa bàn</CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDeleteRegion(regionId),
        },
      );
    },
    [openCrudModal, handleDeleteRegion],
  );

  const handleOpenEditModal = useCallback(
    (region: RegionResponse) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa địa bàn</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              region={region}
              mutate={() => mutate(getRegionListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        500,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const regionActions: ActionItem<RegionResponse>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "region:update",
        onClick: (row) => handleOpenEditModal(row),
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "region:delete",
        onClick: (row) => handleOpenDeleteModal(row.id),
      },
    ],
    [handleOpenEditModal, handleOpenDeleteModal],
  );

  const hasActions = useMemo(
    () => regionActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [regionActions, ability],
  );

  const columns: MRT_ColumnDef<RegionResponse>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã địa bàn",
    },
    {
      accessorKey: "name",
      header: "Tên địa bàn",
    },
    {
      accessorKey: "notes",
      header: "Ghi chú",
      Cell: ({ cell }) => cell.getValue<string>() ?? "—",
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={regionActions} />
            ),
          } satisfies MRT_ColumnDef<RegionResponse>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={regions ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default RegionTable;
